import type { ReactNode } from 'react';
import { AlertCircle, Inbox, RefreshCw, type LucideIcon } from 'lucide-react';
import { ApiRequestError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';

export function LoadingState({
  rows = 4,
  className,
}: {
  rows?: number;
  className?: string;
}) {
  return (
    <div className={cn('space-y-3', className)} role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-14 w-full" />
      ))}
      <span className="sr-only">Loading…</span>
    </div>
  );
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
}: {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-12 text-center',
        className,
      )}
    >
      <Icon className="mb-3 h-9 w-9 text-muted-foreground/60" aria-hidden />
      <p className="text-sm font-medium">{title}</p>
      {description && (
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * Surfaces the server's `errorCode` and `requestId`.
 *
 * The request id is deliberately visible: it is the one string that links a user's
 * "it broke" to the exact server-side path in the logs and the audit trail.
 */
export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const apiError = error instanceof ApiRequestError ? error : null;
  const message =
    apiError?.message ??
    (error instanceof Error ? error.message : 'Something went wrong. Please try again.');

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-xl border border-destructive/30 bg-destructive/5 px-6 py-10 text-center',
        className,
      )}
      role="alert"
    >
      <AlertCircle className="mb-3 h-9 w-9 text-destructive" aria-hidden />
      <p className="text-sm font-medium text-destructive">{message}</p>
      {apiError?.errorCode && (
        <p className="mt-1 font-mono text-xs text-muted-foreground">{apiError.errorCode}</p>
      )}
      {apiError?.requestId && (
        <p className="mt-0.5 font-mono text-[11px] text-muted-foreground/70">
          request {apiError.requestId}
        </p>
      )}
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          <RefreshCw />
          Try again
        </Button>
      )}
    </div>
  );
}
